import QueryString from 'query-string';
import { toastr } from 'react-redux-toastr';
import { push } from 'react-router-redux';
import {
  MAINTENANCE_BASIC_ACTIVITY_ENDPOINT,
  MAINTENANCE_CHECKLIST_ENDPOINT,
} from '../../config/endpoints';
import { DEFAULT_GET_CONFIG, DEFAULT_POST_CONFIG } from '../../config/rest';
import { isErrorResponse } from '../../utils/error-handlers';
import {
  CLEAR_MAINTENANCE_BASIC_ACTIVITIES,
  CLEAR_MAINTENANCE_CHECKLIST,
  CLEAR_MAINTENANCE_CHECKLISTS,
  FLAG_GETTING_MAINTENANCE_BASIC_ACTIVITIES,
  FLAG_GETTING_MAINTENANCE_CHECKLISTS,
  FLAG_MAINTENANCE_CHECKLIST_ACTIVITY,
  GET_MAINTENANCE_BASIC_ACTIVITIES,
  GET_MAINTENANCE_CHECKLIST,
  GET_MAINTENANCE_CHECKLISTS,
} from '../types';
import { MAINTENANCE_CHECKLIST_PATH } from '../../config/paths';

const flagGettingMaintenanceChecklists = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_MAINTENANCE_CHECKLISTS,
    payload: flag,
  });

const getMaintenanceChecklists = async (tableFilters) => async (dispatch) => {
  try {
    dispatch(flagGettingMaintenanceChecklists(true));
    const query = tableFilters;
    const url = `${MAINTENANCE_CHECKLIST_ENDPOINT}?${QueryString.stringify(
      query,
    )}`;
    const promise = await fetch(url, DEFAULT_GET_CONFIG);
    await isErrorResponse(promise, null, dispatch);
    const response = await promise.json();

    dispatch({
      type: GET_MAINTENANCE_CHECKLISTS,
      payload: response,
    });
  } catch ({ message }) {
    toastr.error('Error', message);
  } finally {
    dispatch(flagGettingMaintenanceChecklists(false));
  }
};

const clearMaintenanceChecklists = () => (dispatch) => {
  dispatch({
    type: CLEAR_MAINTENANCE_CHECKLISTS,
  });
};

const flagGettingMaintenanceBasicActivities = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_GETTING_MAINTENANCE_BASIC_ACTIVITIES,
    payload: flag,
  });

const getMaintenanceBasicActivities =
  async (tableFilters) => async (dispatch) => {
    try {
      dispatch(flagGettingMaintenanceBasicActivities(true));
      const query = tableFilters;
      const url = `${MAINTENANCE_BASIC_ACTIVITY_ENDPOINT}?${QueryString.stringify(
        query,
      )}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();

      dispatch({
        type: GET_MAINTENANCE_BASIC_ACTIVITIES,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagGettingMaintenanceBasicActivities(false));
    }
  };

const clearMaintenanceBasicActivities = () => (dispatch) => {
  dispatch({
    type: CLEAR_MAINTENANCE_BASIC_ACTIVITIES,
  });
};

const flagMaintenanceChecklistActivity = (flag) => (dispatch) =>
  dispatch({
    type: FLAG_MAINTENANCE_CHECKLIST_ACTIVITY,
    payload: flag,
  });

const createNewMaintenanceChecklist = async ({
  registeredBusId,
  driverId,
  deadline,
  maintenanceChecklistBasicActivityExecutionList,
  maintenanceChecklistAdditionalActivityExecutionList,
}) => {
  const payload = {
    registeredBusId,
    driverId,
    deadline,
    maintenanceChecklistBasicActivityExecutionList,
    maintenanceChecklistAdditionalActivityExecutionList,
  };

  const url = MAINTENANCE_CHECKLIST_ENDPOINT;

  const promise = await fetch(url, {
    ...DEFAULT_POST_CONFIG,
    body: JSON.stringify(payload),
  });

  await isErrorResponse(promise);

  const maintenanceChecklist = await promise.json();

  return maintenanceChecklist;
};

const postMaintenanceChecklist =
  async ({
    registeredBusId,
    driverId,
    deadline,
    maintenanceChecklistBasicActivityExecutionList,
    maintenanceChecklistAdditionalActivityExecutionList,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceChecklistActivity(true));

      const response = await createNewMaintenanceChecklist({
        registeredBusId,
        driverId,
        deadline,
        maintenanceChecklistBasicActivityExecutionList,
        maintenanceChecklistAdditionalActivityExecutionList,
      });

      if (response) {
        dispatch(push(`${MAINTENANCE_CHECKLIST_PATH}/${response.id}`));
      }
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceChecklistActivity(false));
    }
  };

const getMaintenanceChecklist =
  async ({ maintenanceChecklistId }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceChecklistActivity(true));
      const url = `${MAINTENANCE_CHECKLIST_ENDPOINT}/${maintenanceChecklistId}`;
      const promise = await fetch(url, DEFAULT_GET_CONFIG);
      await isErrorResponse(promise, null, dispatch);
      const response = await promise.json();
      dispatch({
        type: GET_MAINTENANCE_CHECKLIST,
        payload: response,
      });
    } catch ({ message }) {
      toastr.error('Error', message);
    } finally {
      dispatch(flagMaintenanceChecklistActivity(false));
    }
  };

const clearMaintenanceChecklist = () => (dispatch) =>
  dispatch({
    type: CLEAR_MAINTENANCE_CHECKLIST,
  });

const updateMaintenanceChecklistBasicActivities = async ({
  maintenanceChecklistId,
  maintenanceChecklistBasicActivityExecutionList,
}) => {
  const url = `${MAINTENANCE_CHECKLIST_ENDPOINT}/${maintenanceChecklistId}/basic-activities`;

  const promise = await fetch(url, {
    ...DEFAULT_POST_CONFIG,
    method: 'PUT',
    body: JSON.stringify(maintenanceChecklistBasicActivityExecutionList),
  });

  await isErrorResponse(promise);

  const maintenanceChecklist = await promise.json();

  return maintenanceChecklist;
};

const putMaintenanceChecklistBasicActivities =
  async ({
    maintenanceChecklistId,
    maintenanceChecklistBasicActivityExecutionList,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceChecklistActivity(true));

      const response = await updateMaintenanceChecklistBasicActivities({
        maintenanceChecklistId,
        maintenanceChecklistBasicActivityExecutionList,
      });

      if (response) {
        toastr.success(
          'Checklist básico',
          'Checklist básico registrado correctamente.',
        );

        dispatch({
          type: GET_MAINTENANCE_CHECKLIST,
          payload: response,
        });
      }

      return response;
    } catch ({ message }) {
      toastr.error('Error', message);
      return null;
    } finally {
      dispatch(flagMaintenanceChecklistActivity(false));
    }
  };

const updateMaintenanceChecklistAdditionalExecution = async ({
  maintenanceChecklistId,
  maintenanceChecklistAdditionalActivityExecutionUpdateRequest,
}) => {
  const url = `${MAINTENANCE_CHECKLIST_ENDPOINT}/${maintenanceChecklistId}/additional-execution`;

  const promise = await fetch(url, {
    ...DEFAULT_POST_CONFIG,
    method: 'PUT',
    body: JSON.stringify(
      maintenanceChecklistAdditionalActivityExecutionUpdateRequest,
    ),
  });

  await isErrorResponse(promise);

  const maintenanceChecklistActivity = await promise.json();

  return maintenanceChecklistActivity;
};

const putMaintenanceChecklistAdditionalExecution =
  async ({
    maintenanceChecklistId,
    maintenanceChecklistAdditionalActivityExecutionUpdateRequest,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceChecklistActivity(true));

      const response = await updateMaintenanceChecklistAdditionalExecution({
        maintenanceChecklistId,
        maintenanceChecklistAdditionalActivityExecutionUpdateRequest,
      });

      if (response) {
        toastr.success(
          'ejecución de adicionales',
          'La ejecución de adicional actualizado correctamente.',
        );
      }

      return response;
    } catch ({ message }) {
      toastr.error('Error', message);
      return null;
    } finally {
      dispatch(flagMaintenanceChecklistActivity(false));
    }
  };

const addMaintenanceChecklistAdditionalActivities = async ({
  maintenanceChecklistId,
  maintenanceChecklistAdditionalActivityExecutionList,
}) => {
  const url = `${MAINTENANCE_CHECKLIST_ENDPOINT}/${maintenanceChecklistId}/additional-activities`;

  const promise = await fetch(url, {
    ...DEFAULT_POST_CONFIG,
    method: 'POST',
    body: JSON.stringify(maintenanceChecklistAdditionalActivityExecutionList),
  });

  await isErrorResponse(promise);

  const maintenanceChecklist = await promise.json();

  return maintenanceChecklist;
};

const postMaintenanceChecklistAdditionalActivities =
  ({
    maintenanceChecklistId,
    maintenanceChecklistAdditionalActivityExecutionList,
  }) =>
  async (dispatch) => {
    try {
      dispatch(flagMaintenanceChecklistActivity(true));

      const response = await addMaintenanceChecklistAdditionalActivities({
        maintenanceChecklistId,
        maintenanceChecklistAdditionalActivityExecutionList,
      });

      if (response) {
        toastr.success(
          'Actividades adicionales',
          'Las actividades adicionales fueron registradas correctamente.',
        );

        dispatch({
          type: GET_MAINTENANCE_CHECKLIST,
          payload: response,
        });
      }

      return response;
    } catch ({ message }) {
      toastr.error('Error', message);
      return null;
    } finally {
      dispatch(flagMaintenanceChecklistActivity(false));
    }
  };

export {
  getMaintenanceBasicActivities,
  clearMaintenanceBasicActivities,
  postMaintenanceChecklist,
  getMaintenanceChecklist,
  clearMaintenanceChecklist,
  getMaintenanceChecklists,
  clearMaintenanceChecklists,
  putMaintenanceChecklistBasicActivities,
  putMaintenanceChecklistAdditionalExecution,
  postMaintenanceChecklistAdditionalActivities,
};
